/**
 * ImagePreloader Service
 * Preloads and caches map images/icons
 * Uses requestIdleCallback for non-blocking loads
 *
 * Also acts as an IconRegistry: maintains a mapping from logical icon keys
 * (e.g. 'military:research', 'vessel:cargo') to Mapbox image IDs and source URLs
 * so each PNG/WEBP is loaded at most once per session.
 */

import portIcon from '@/assets/icons/anchor3.png';
import military_communication from '@/assets/icons/chinese_communication.png';
import military_energy from '@/assets/icons/chinese_energy.png';
import military_logistics from '@/assets/icons/chinese_logistics.png';
import military_nuclear from '@/assets/icons/chinese_nuclear.png';
// Military icon imports
import military_research from '@/assets/icons/chinese_research.png';
import military_satellite from '@/assets/icons/chinese_satellite.png';
import military_training from '@/assets/icons/chinese_training.png';
import military_other from '@/assets/icons/chinese-symbol.png';
// Incident / port icon imports
import incidentIcon from '@/assets/icons/incidents.png';
import { ICON_PATHS } from '@/constants/mapConstants';
import { mapManager } from './MapManager';

interface ImageLoadResult {
  success: boolean;
  image?: HTMLImageElement | ImageBitmap;
  error?: Error;
}

/**
 * Icon definition: maps a logical key to a Mapbox image ID and source URL.
 */
export interface IconDefinition {
  id: string;
  url: string;
}

/**
 * Central registry of all custom icons used by map layers.
 * Each entry maps a logical key to a unique Mapbox image ID and the URL to load.
 * Using a single ID per URL ensures the image is loaded only once.
 */
const ICON_REGISTRY: Record<string, IconDefinition> = {
  // Military icons
  'military:research': { id: 'military-research', url: military_research },
  'military:energy': { id: 'military-energy', url: military_energy },
  'military:command': { id: 'military-command', url: military_communication },
  'military:logistics': { id: 'military-logistics', url: military_logistics },
  'military:nuclear': { id: 'military-nuclear', url: military_nuclear },
  'military:training': { id: 'military-training', url: military_training },
  'military:satellite': { id: 'military-satellite', url: military_satellite },
  'military:other': { id: 'military-other', url: military_other },

  // Incident icon
  'incident:default': { id: 'incident-icon', url: incidentIcon },

  // Port icon
  'port:default': { id: 'portIcon', url: portIcon },

  // Vessel icons (paths constructed from ICON_PATHS)
  'vessel:cargo': { id: 'cargo', url: `${ICON_PATHS.VESSELS}cargo.png` },
  'vessel:tanker': { id: 'tanker', url: `${ICON_PATHS.VESSELS}tanker.png` },
  'vessel:passenger': { id: 'passenger', url: `${ICON_PATHS.VESSELS}passenger.png` },
  'vessel:other_type': { id: 'other_type', url: `${ICON_PATHS.VESSELS}other_type.png` },
  'vessel:reserved_for_future_use': {
    id: 'reserved_for_future_use',
    url: `${ICON_PATHS.VESSELS}reserved_for_future_use.png`,
  },
  'vessel:wing_in_ground': { id: 'wing_in_ground', url: `${ICON_PATHS.VESSELS}wing_in_ground.png` },
  'vessel:high_speed_craft': { id: 'high_speed_craft', url: `${ICON_PATHS.VESSELS}high_speed_craft.png` },
  'vessel:unknown_type': { id: 'unknown_type', url: `${ICON_PATHS.VESSELS}unknown_type.png` },
};

/**
 * Map military general_category values to registry keys.
 */
const MILITARY_CATEGORY_TO_KEY: Record<string, string> = {
  'Research and Development': 'military:research',
  'Energy Infrastructure': 'military:energy',
  'Command and Control': 'military:command',
  Logistics: 'military:logistics',
  'Nuclear Facilities': 'military:nuclear',
  Training: 'military:training',
  'Space and Surveillance': 'military:satellite',
  Other: 'military:other',
};

class ImagePreloader {
  private static instance: ImagePreloader;
  private loadedImages: Set<string> = new Set();
  private loadingImages: Map<string, Promise<ImageLoadResult>> = new Map();
  private imageCache: Map<string, HTMLImageElement | ImageBitmap> = new Map();

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): ImagePreloader {
    if (!ImagePreloader.instance) {
      ImagePreloader.instance = new ImagePreloader();
    }
    return ImagePreloader.instance;
  }

  /**
   * Load a single image
   * @param imagePath - Path to the image (URL string or imported image URL)
   * @param imageId - Optional ID for the image (used in Mapbox)
   * @param mapInstance - Optional map instance (if MapManager not initialized)
   */
  private async loadImage(
    imagePath: string,
    imageId?: string,
    mapInstance?: mapboxgl.Map | null,
  ): Promise<ImageLoadResult> {
    const id = imageId || imagePath;

    // Check if already loaded
    if (this.loadedImages.has(id)) {
      const cached = this.imageCache.get(id);
      if (cached) {
        return { success: true, image: cached };
      }
    }

    // Check if currently loading
    const loading = this.loadingImages.get(id);
    if (loading) {
      return loading;
    }

    // Start loading
    const loadPromise = new Promise<ImageLoadResult>((resolve) => {
      // Try to get map from MapManager or use provided instance
      const map = mapInstance || mapManager.getMap();
      if (!map) {
        resolve({ success: false, error: new Error('Map not initialized') });
        return;
      }

      // Check if image already exists in map
      if (map.hasImage(id)) {
        this.loadedImages.add(id);
        resolve({ success: true });
        return;
      }

      // Ensure style is loaded before loading images
      if (!map.isStyleLoaded()) {
        map.once('style.load', () => {
          this.loadImage(imagePath, imageId, mapInstance).then(resolve);
        });
        return;
      }

      // For imported images (ES6 imports), use browser Image API first
      // This is more reliable than map.loadImage for Vite-processed URLs
      const isImportedUrl = imagePath.startsWith('/') || imagePath.startsWith('http') || imagePath.includes('assets');

      if (isImportedUrl) {
        // Use browser Image API for better compatibility
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
          try {
            // Ensure map is still valid and style is loaded
            if (!map.isStyleLoaded()) {
              this.loadingImages.delete(id);
              resolve({ success: false, error: new Error('Map style not loaded') });
              return;
            }

            // Add image to map if not already added
            if (!map.hasImage(id)) {
              try {
                map.addImage(id, img);
              } catch (addError) {
                console.warn(`ImagePreloader: Error adding image ${id}:`, addError);
                this.loadingImages.delete(id);
                resolve({ success: false, error: addError as Error });
                return;
              }
            }

            // Cache image
            this.imageCache.set(id, img);
            this.loadedImages.add(id);
            this.loadingImages.delete(id);

            resolve({ success: true, image: img });
          } catch (error) {
            this.loadingImages.delete(id);
            resolve({ success: false, error: error as Error });
          }
        };

        img.onerror = (_error) => {
          this.loadingImages.delete(id);
          // Fallback to map.loadImage if browser Image API fails
          map.loadImage(imagePath, (mapError, mapImage) => {
            if (mapError || !mapImage) {
              resolve({
                success: false,
                error: new Error(`Failed to load image: ${imagePath}`),
              });
              return;
            }

            try {
              // Type assertion: mapbox loadImage returns a valid image type for addImage
              const validImage = mapImage as HTMLImageElement | ImageBitmap;
              if (!map.hasImage(id)) {
                map.addImage(id, validImage);
              }
              this.imageCache.set(id, validImage);
              this.loadedImages.add(id);
              this.loadingImages.delete(id);
              resolve({ success: true, image: validImage });
            } catch (addError) {
              this.loadingImages.delete(id);
              resolve({ success: false, error: addError as Error });
            }
          });
        };

        img.src = imagePath;
      } else {
        // For relative paths, use Mapbox loadImage API
        map.loadImage(imagePath, (error, loadedImage) => {
          if (error || !loadedImage) {
            this.loadingImages.delete(id);
            resolve({
              success: false,
              error: error || new Error('Failed to load image'),
            });
            return;
          }

          // Ensure map is still valid and style is loaded
          if (!map.isStyleLoaded()) {
            this.loadingImages.delete(id);
            resolve({ success: false, error: new Error('Map style not loaded') });
            return;
          }

          // Type assertion: mapbox loadImage returns a valid image type for addImage
          const image = loadedImage as HTMLImageElement | ImageBitmap;

          // Add image to map if not already added
          if (!map.hasImage(id)) {
            try {
              map.addImage(id, image);
            } catch (addError) {
              console.warn(`ImagePreloader: Error adding image ${id}:`, addError);
              this.loadingImages.delete(id);
              resolve({ success: false, error: addError as Error });
              return;
            }
          }

          // Cache image
          this.imageCache.set(id, image);
          this.loadedImages.add(id);
          this.loadingImages.delete(id);

          resolve({ success: true, image });
        });
      }
    });

    this.loadingImages.set(id, loadPromise);
    return loadPromise;
  }

  /**
   * Preload a single image
   * @param imagePath - Path to the image
   * @param imageId - Optional ID for the image
   */
  async preloadImage(imagePath: string, imageId?: string): Promise<boolean> {
    const result = await this.loadImage(imagePath, imageId);
    return result.success;
  }

  /**
   * Preload multiple images
   * @param images - Array of image paths or {path, id} objects
   */
  async preloadImages(
    images: Array<string | { path: string; id: string }>,
  ): Promise<{ success: number; failed: number }> {
    let success = 0;
    let failed = 0;

    const loadPromises = images.map(async (img) => {
      if (typeof img === 'string') {
        const result = await this.loadImage(img);
        if (result.success) {
          success++;
        } else {
          failed++;
        }
      } else {
        const result = await this.loadImage(img.path, img.id);
        if (result.success) {
          success++;
        } else {
          failed++;
        }
      }
    });

    await Promise.all(loadPromises);

    return { success, failed };
  }

  /**
   * Preload vessel icons
   * Loads icons immediately to ensure they're ready when needed
   * @param map - Optional map instance (if MapManager not initialized yet)
   */
  async preloadVesselIcons(mapInstance?: mapboxgl.Map | null): Promise<{ success: number; failed: number }> {
    // Try to get map from MapManager first, or use provided instance
    const map = mapInstance || mapManager.getMap();
    if (!map) {
      console.warn('ImagePreloader: Map not initialized');
      return { success: 0, failed: ICON_PATHS.VESSEL_TYPES.length };
    }

    // Ensure style is loaded before loading images
    if (!map.isStyleLoaded()) {
      await new Promise<void>((resolve) => {
        map.once('style.load', () => resolve());
      });
    }

    const loadPromises = ICON_PATHS.VESSEL_TYPES.map((iconType) => {
      const iconId = iconType;

      // If already tracked as loaded or present in the map, count as success
      if (this.loadedImages.has(iconId) || map.hasImage(iconId)) {
        this.loadedImages.add(iconId);
        return Promise.resolve({ success: true });
      }

      const iconPath = `${ICON_PATHS.VESSELS}${iconType}.png`;
      return this.loadImage(iconPath, iconId, map);
    });

    const results = await Promise.all(loadPromises);

    const success = results.filter((r) => r.success).length;
    const failed = results.length - success;

    return { success, failed };
  }

  /**
   * Check if an image is loaded
   * @param imageId - Image identifier
   */
  isImageLoaded(imageId: string): boolean {
    return this.loadedImages.has(imageId);
  }

  /**
   * Get cached image
   * @param imageId - Image identifier
   */
  getCachedImage(imageId: string): HTMLImageElement | ImageBitmap | undefined {
    return this.imageCache.get(imageId);
  }

  /**
   * Clear image cache
   */
  clearCache(): void {
    this.loadedImages.clear();
    this.loadingImages.clear();
    this.imageCache.clear();
  }

  /**
   * Reset the preloader (for testing or re-initialization)
   */
  reset(): void {
    this.clearCache();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Icon Registry API
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Resolve a military category to its icon definition.
   * Falls back to 'military:other' for unknown categories.
   */
  resolveMilitaryIcon(category: string): IconDefinition {
    const key = MILITARY_CATEGORY_TO_KEY[category] || 'military:other';
    return ICON_REGISTRY[key];
  }

  /**
   * Get the registry key for a military category.
   * Falls back to 'military:other' for unknown categories.
   */
  getMilitaryIconKey(category: string): string {
    return MILITARY_CATEGORY_TO_KEY[category] || 'military:other';
  }

  /**
   * Ensure a military icon is loaded by category name.
   * Convenience wrapper around ensureIconLoaded.
   *
   * @param map - Mapbox map instance
   * @param category - Military category (e.g. 'Research and Development')
   * @returns Promise resolving to the Mapbox image ID
   */
  async ensureMilitaryIconLoaded(map: mapboxgl.Map | null, category: string): Promise<string> {
    const key = this.getMilitaryIconKey(category);
    return this.ensureIconLoaded(map, key);
  }

  /**
   * Resolve a vessel type name (e.g. "Cargo", "Tanker") to its icon definition.
   * Falls back to 'vessel:other_type' for unknown types.
   */
  resolveVesselIcon(vesselType: string): IconDefinition {
    // Normalize type name to registry key format
    const normalized = vesselType.toLowerCase().replace(/\s+/g, '_').replace(/[()]/g, '');
    const key = `vessel:${normalized}`;
    return ICON_REGISTRY[key] || ICON_REGISTRY['vessel:other_type'];
  }

  /**
   * Get an icon definition by registry key (e.g. 'military:research', 'vessel:cargo').
   */
  getIconDefinition(key: string): IconDefinition | undefined {
    return ICON_REGISTRY[key];
  }

  /**
   * Ensure an icon is loaded in the map by its registry key.
   * If the image is already present (by ID), returns immediately.
   * Otherwise loads the image from the URL and adds it under the ID.
   *
   * @param map - Mapbox map instance
   * @param key - Registry key (e.g. 'military:research')
   * @returns Promise resolving to the Mapbox image ID
   */
  async ensureIconLoaded(map: mapboxgl.Map | null, key: string): Promise<string> {
    const def = ICON_REGISTRY[key];
    if (!def) {
      console.warn(`ImagePreloader: Unknown icon key "${key}"`);
      return '';
    }

    const { id, url } = def;

    // Already present in map
    if (map.hasImage(id)) {
      this.loadedImages.add(id);
      return id;
    }

    // Already tracked as loaded (but perhaps map was reset)
    if (this.loadedImages.has(id) && !map.hasImage(id)) {
      // Re-add from cache if available
      const cached = this.imageCache.get(id);
      if (cached) {
        try {
          map.addImage(id, cached);
        } catch {
          // ignore if already added
        }
        return id;
      }
    }

    // Load from URL
    const result = await this.loadImage(url, id, map);
    return result.success ? id : '';
  }

  /**
   * Preload all icons from the registry.
   * Call once after map style is loaded to front-load all custom icons.
   *
   * @param map - Mapbox map instance
   */
  async preloadAllIcons(map: mapboxgl.Map | null): Promise<{ success: number; failed: number }> {
    if (!map || !map.isStyleLoaded()) {
      return { success: 0, failed: Object.keys(ICON_REGISTRY).length };
    }

    const keys = Object.keys(ICON_REGISTRY);
    const results = await Promise.all(
      keys.map(async (key) => {
        const id = await this.ensureIconLoaded(map, key);
        return id !== '';
      }),
    );

    const success = results.filter(Boolean).length;
    return { success, failed: keys.length - success };
  }
}

// Export singleton instance
export const imagePreloader = ImagePreloader.getInstance();
